import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, Button, Input, List, Space, Typography, Tag, Modal, Tooltip, message, Divider, Table, Dropdown } from 'antd';
import { CopyOutlined, UserOutlined, PlusOutlined, TeamOutlined, HistoryOutlined, BulbOutlined, EllipsisOutlined } from '@ant-design/icons';
import { socket } from '../socket';
import VotingScreen from '../components/VotingScreen';
import SessionTimer from '../components/SessionTimer';
import QuestionsPanel from '../components/QuestionsPanel';
import AIAssistant from '../components/AIAssistant';
import ReactMarkdown from 'react-markdown';

const { Title, Text, Paragraph } = Typography;

const estimationTypes = {
  fibonacci: ['1', '2', '3', '5', '8', '13', '21'],
  tShirt: ['XS', 'S', 'M', 'L', 'XL', 'XXL'],
  linear: ['1', '2', '3', '4', '5', '6', '7', '8'],
  hours: ['0.5', '1', '2', '4', '6', '8', '12', '16']
};

function Channel() {
  const { channelId } = useParams();
  const navigate = useNavigate();
  const [channel, setChannel] = useState(null);
  const [newStory, setNewStory] = useState('');
  const [newStoryDescription, setNewStoryDescription] = useState('');
  const [selectedVote, setSelectedVote] = useState(null);
  const [showVotes, setShowVotes] = useState(false);
  const [isVoting, setIsVoting] = useState(false);
  const [quickVote, setQuickVote] = useState(false);
  const [questions, setQuestions] = useState([]);
  const [showAIAssistant, setShowAIAssistant] = useState(false);
  const [currentStory, setCurrentStory] = useState(null);
  const [isCreator, setIsCreator] = useState(false);
  const [votesRevealed, setVotesRevealed] = useState(false);

  useEffect(() => {
    socket.emit('getChannel', { channelId });

    socket.on('channelData', (channel) => {
      if (channel.votes) {
        channel.votes = new Map(Array.isArray(channel.votes) ? channel.votes : Object.entries(channel.votes));
      }
      setChannel(channel);
      setIsCreator(channel.creator === socket.id);
    });

    socket.on('channelUpdated', (channel) => {
      if (channel.votes) {
        channel.votes = new Map(Array.isArray(channel.votes) ? channel.votes : Object.entries(channel.votes));
      }
      setChannel(channel);
    });

    socket.on('votesRevealed', () => {
      setVotesRevealed(true);
    });

    socket.on('votingStarted', ({ channel }) => {
      if (channel.votes) {
        channel.votes = new Map(Array.isArray(channel.votes) ? channel.votes : Object.entries(channel.votes));
      }
      setChannel(channel);
      setShowVotes(false);
      setSelectedVote(null);
      setIsVoting(true);
      setVotesRevealed(false);
    });

    socket.on('voteSubmitted', ({ votes, complete }) => {
      setChannel(prev => ({
        ...prev,
        votes: new Map(Array.isArray(votes) ? votes : Object.entries(votes)),
        votingComplete: complete
      }));
    });

    socket.on('votingFinished', () => {
      setVotesRevealed(false);
    });

    return () => {
      socket.off('channelData');
      socket.off('channelUpdated');
      socket.off('votesRevealed');
      socket.off('votingStarted');
      socket.off('voteSubmitted');
      socket.off('votingFinished');
    };
  }, [channelId]);

  const addStory = () => {
    if (!newStory.trim()) {
      message.error('Please enter a story name');
      return;
    }
    socket.emit('addStory', {
      channelId,
      story: { 
        name: newStory,
        description: newStoryDescription 
      }
    });
    setNewStory('');
    setNewStoryDescription('');
  };

  const startVoting = (storyId) => {
    setIsVoting(true);
    socket.emit('startVoting', { channelId, storyId });
  };

  const submitVote = (value) => {
    setSelectedVote(value);
    socket.emit('submitVote', { channelId, vote: value });
  };

  const revealVotes = () => {
    socket.emit('revealVotes', { channelId });
  };

  const calculateAverage = (votes) => {
    if (!votes) return 0;
    
    // Ensure we're working with an array if it's not already a Map
    let votesArray = votes instanceof Map ? Array.from(votes.values()) : 
                     Array.isArray(votes) ? votes.map(([_, vote]) => vote) : [];
    
    if (votesArray.length === 0) return 0;
    
    const numericVotes = votesArray.filter(v => !isNaN(parseFloat(v)));
    if (numericVotes.length === 0) return 'N/A';
    
    return (numericVotes.reduce((a, b) => parseFloat(a) + parseFloat(b), 0) / numericVotes.length).toFixed(1);
  };

  const copyInviteLink = () => {
    const link = `${window.location.origin}/join/${channelId}`;
    navigator.clipboard.writeText(link);
    message.success('Invite link copied to clipboard!');
  };

  const startQuickVote = () => {
    setQuickVote(true);
    setIsVoting(true);
    socket.emit('startVoting', { channelId, storyId: null });
  };

  const closeVoting = () => {
    if (isCreator) {
      socket.emit('finishVoting', { channelId });
    }
    setIsVoting(false);
    setQuickVote(false);
    setShowVotes(false);
    setSelectedVote(null);
  };

  const handleAddQuestion = (question) => {
    setQuestions([...questions, question]);
  };

  const handleDeleteQuestion = (index) => {
    setQuestions(questions.filter((_, i) => i !== index));
  };

  const handleGenerateAI = async (story) => {
    if (!isCreator) return;
    setShowAIAssistant(true);
    setCurrentStory(story);
  };

  const handleNextStory = () => {
    socket.emit('nextStory', { channelId });
  };

  const handleUpdateStoryDescription = (storyId, newDescription) => {
    socket.emit('updateStoryDescription', { 
      channelId, 
      storyId, 
      newDescription 
    });
  };

  if (!channel) return <div>Loading...</div>;

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto', padding: '24px' }}>
      {/* Header Section */}
      <Card 
        style={{ 
          marginBottom: 24,
          background: 'linear-gradient(to right, rgba(102, 102, 102, 0.05), rgba(102, 102, 102, 0.02))',
          border: '1px solid rgba(102, 102, 102, 0.1)'
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <Title level={2} style={{ margin: '0 0 8px 0' }}>{channel.name}</Title>
            <Space split={<Divider type="vertical" />}>
              <Text type="secondary">
                <TeamOutlined /> {channel.users.length} participants
              </Text>
              <Text type="secondary">
                <HistoryOutlined /> {channel.stories.length} stories
              </Text>
              <Text type="secondary">
                Mode: {channel.estimationType.charAt(0).toUpperCase() + channel.estimationType.slice(1)}
              </Text>
            </Space>
          </div>
          <Space size="middle">
            <Tooltip title="Copy invite link">
              <Button 
                icon={<CopyOutlined />} 
                onClick={copyInviteLink}
                style={{ borderRadius: '50%', width: 40, height: 40 }}
              />
            </Tooltip>
            <Button type="primary" onClick={startQuickVote} size="large">
              Quick Vote
            </Button>
          </Space>
        </div>
      </Card>

      <div style={{ display: 'flex', gap: 24 }}>
        {/* Main Content */}
        <div style={{ flex: 1 }}>
          {/* Story Input */}
          <Card
            title="User Stories"
            style={{ marginBottom: 24, padding: '24px' }}
          >
            {isCreator && (
              <Space direction="vertical" style={{ width: '100%', marginBottom: 24 }}>
                <Input
                  value={newStory}
                  onChange={e => setNewStory(e.target.value)}
                  placeholder="Enter user story title"
                  size="large"
                  style={{ padding: '12px' }}
                />
                <Input.TextArea
                  value={newStoryDescription}
                  onChange={e => setNewStoryDescription(e.target.value)}
                  placeholder="Enter story description (optional)"
                  rows={3}
                  style={{ padding: '12px', marginTop: 16 }}
                />
                <Button 
                  type="primary" 
                  onClick={addStory} 
                  icon={<PlusOutlined />}
                  block
                  size="large"
                  style={{ marginTop: 16, height: 48 }}
                >
                  Add Story
                </Button>
              </Space>
            )}

            <Table
              dataSource={channel.stories}
              rowKey="id"
              style={{ marginTop: 24 }}
              columns={[
                {
                  title: '',
                  key: 'actions',
                  width: 50,
                  render: (_, record) => (
                    isCreator && (
                      <Dropdown
                        menu={{
                          items: [
                            {
                              key: 'vote',
                              label: 'Start Voting',
                              onClick: () => startVoting(record.id),
                              disabled: channel.currentStory === record.id
                            },
                            {
                              key: 'ai',
                              label: 'AI Assist',
                              onClick: () => handleGenerateAI(record)
                            }
                          ]
                        }}
                        trigger={['click']}
                      >
                        <Button
                          icon={<EllipsisOutlined />}
                          type="text"
                        />
                      </Dropdown>
                    )
                  ),
                },
                {
                  title: '',
                  key: 'estimate',
                  width: 80,
                  render: (_, record) => {
                    if (record.id === channel.currentStory) {
                      return <Tag color="processing">Voting...</Tag>;
                    }
                    if (record.finalEstimate) {
                      return (
                        <Tag color="success" style={{ fontSize: '14px', padding: '4px 8px' }}>
                          {record.finalEstimate}
                        </Tag>
                      );
                    }
                    return null;
                  },
                },
                {
                  title: 'Story Details',
                  dataIndex: 'name',
                  key: 'name',
                  render: (text, record) => (
                    <div>
                      <Text strong>{text}</Text>
                      {record.description && (
                        <div className="markdown-content" style={{ marginTop: 8 }}>
                          <ReactMarkdown>
                            {record.description}
                          </ReactMarkdown>
                        </div>
                      )}
                    </div>
                  ),
                }
              ]}
            />
          </Card>
        </div>

        {/* Updated Sidebar */}
        <div style={{ width: 300 }}>
          <Space direction="vertical" style={{ width: '100%' }} size="large">
            <Card 
              title={<Space><TeamOutlined /> Participants</Space>}
              style={{ 
                background: 'rgba(255, 255, 255, 0.8)',
                backdropFilter: 'blur(5px)'
              }}
            >
              <Space wrap>
                {channel.users.map(user => (
                  <Tag 
                    key={user.id} 
                    color={user.id === socket.id ? 'blue' : 'default'}
                    style={{ 
                      padding: '4px 8px', 
                      margin: '4px',
                      borderRadius: 4
                    }}
                  >
                    {user.name}
                  </Tag>
                ))}
              </Space>
            </Card>

            <Card 
              style={{ 
                background: 'rgba(255, 255, 255, 0.8)',
                backdropFilter: 'blur(5px)'
              }}
            >
              <QuestionsPanel
                questions={questions}
                onAddQuestion={handleAddQuestion}
                onDeleteQuestion={handleDeleteQuestion}
              />
            </Card>
          </Space>
        </div>
      </div>

      {isVoting && (
        <VotingScreen
          story={channel.stories.find(s => s.id === channel.currentStory)}
          estimationType={channel.estimationType}
          onVote={submitVote}
          selectedVote={selectedVote}
          onClose={closeVoting}
          showVotes={showVotes}
          votes={channel.votes}
          users={channel.users}
          average={calculateAverage(channel.votes)}
          onRevealVotes={revealVotes}
          votingComplete={channel.votingComplete}
          isCreator={isCreator}
          onGenerateAI={handleGenerateAI}
          questions={questions}
          onAddQuestion={handleAddQuestion}
          onDeleteQuestion={handleDeleteQuestion}
          onNextStory={handleNextStory}
          hasNextStory={channel.stories.findIndex(s => s.id === channel.currentStory) < channel.stories.length - 1}
          votesRevealed={votesRevealed}
          channel={channel}
        />
      )}

      {/* Update AI Assistant Modal */}
      {showAIAssistant && currentStory && (
        <Modal
          visible={showAIAssistant}
          onCancel={() => setShowAIAssistant(false)}
          footer={null}
          width={800}
        >
          <AIAssistant
            storyName={currentStory.name}
            storyDescription={currentStory.description}
            storyId={currentStory.id}
            onUpdateDescription={handleUpdateStoryDescription}
            onClose={() => setShowAIAssistant(false)}
          />
        </Modal>
      )}
    </div>
  );
}

export default Channel; 